import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Send, Phone, ExternalLink, Info } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const WHATSAPP_NUMBER = '5527996482098';

const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', company: '', email: '', message: '' });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const buildWhatsappUrl = () => {
    const lines = [
      t('Olá, gostaria de conversar com a Costa Valente.', 'Hello, I would like to contact Costa Valente.'),
      '',
      `${t('Nome', 'Name')}: ${formData.name || '—'}`,
      `${t('Empresa', 'Company')}: ${formData.company || '—'}`,
      `Email: ${formData.email || '—'}`,
      '',
      `${t('Mensagem', 'Message')}: ${formData.message || '—'}`,
    ];
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join('\n'))}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Enquanto a integração real com Resend não estiver ativa, o envio
    // é redirecionado ao WhatsApp com o conteúdo do formulário — sem simular sucesso.
    window.open(buildWhatsappUrl(), '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen pt-24 bg-white">
      <section className="py-16 md:py-20 bg-white border-b border-[#E6E6ED]">
        <div className="cv-container">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-brass"></span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
                {t('CONTATO', 'CONTACT')}
              </span>
            </div>
            <h1 className="text-[40px] sm:text-5xl lg:text-[56px] tracking-tight font-bold text-abyssal leading-[1.05] mb-6">
              {t('Fale ', 'Get in ')}<span className="text-cobalt">{t('conosco.', 'touch.')}</span>
            </h1>
            <p className="text-lg text-steel leading-relaxed max-w-2xl">
              {t(
                'Conte brevemente o que sua empresa pretende importar. Nossa equipe analisará o cenário e entrará em contato.',
                'Briefly tell us what your company intends to import. Our team will analyze the scenario and get in touch.'
              )}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-mist">
        <div className="cv-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="text-2xl font-bold text-abyssal mb-3">
                {t('Envie sua mensagem', 'Send your message')}
              </h2>
              <p className="text-[15px] text-steel leading-relaxed mb-6">
                {t(
                  'Preencha os campos abaixo — ao enviar, abriremos o WhatsApp com o conteúdo já pronto para nossa equipe.',
                  'Fill in the fields below — when you send, we will open WhatsApp with the content ready for our team.'
                )}
              </p>
              <div
                data-testid="contact-transparency-notice"
                className="flex items-start gap-3 bg-cobalt-100 border border-cobalt/20 rounded-md p-4 mb-6"
              >
                <Info className="h-5 w-5 text-cobalt flex-shrink-0 mt-0.5" />
                <p className="text-sm text-abyssal leading-relaxed">
                  {t(
                    'Ao enviar, você será direcionado ao WhatsApp da Costa Valente com as informações preenchidas para iniciar o atendimento.',
                    'On submit, you will be redirected to Costa Valente\u2019s WhatsApp with your information ready to start the conversation.'
                  )}
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div>
                  <Label htmlFor="name" className="text-abyssal text-sm font-medium">
                    {t('Nome', 'Name')} *
                  </Label>
                  <Input
                    id="name" name="name" data-testid="contact-input-name"
                    value={formData.name} onChange={handleChange} required
                    className="mt-2 bg-white border-[#E6E6ED] text-abyssal focus:border-cobalt focus:ring-cobalt/20"
                    placeholder={t('Seu nome completo', 'Your full name')}
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-abyssal text-sm font-medium">
                    {t('Empresa', 'Company')} *
                  </Label>
                  <Input
                    id="company" name="company" data-testid="contact-input-company"
                    value={formData.company} onChange={handleChange} required
                    className="mt-2 bg-white border-[#E6E6ED] text-abyssal focus:border-cobalt focus:ring-cobalt/20"
                    placeholder={t('Nome da empresa', 'Company name')}
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-abyssal text-sm font-medium">Email *</Label>
                  <Input
                    id="email" name="email" type="email" data-testid="contact-input-email"
                    value={formData.email} onChange={handleChange} required
                    className="mt-2 bg-white border-[#E6E6ED] text-abyssal focus:border-cobalt focus:ring-cobalt/20"
                    placeholder={t('seu@email.com', 'your@email.com')}
                  />
                </div>
                <div>
                  <Label htmlFor="message" className="text-abyssal text-sm font-medium">
                    {t('Mensagem', 'Message')} *
                  </Label>
                  <Textarea
                    id="message" name="message" data-testid="contact-input-message"
                    value={formData.message} onChange={handleChange} required rows={5}
                    className="mt-2 bg-white border-[#E6E6ED] text-abyssal resize-none focus:border-cobalt focus:ring-cobalt/20"
                    placeholder={t('Conte-nos sobre sua operação de importação...', 'Tell us about your import operation...')}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  className="w-full flex items-center justify-center gap-2 bg-abyssal hover:bg-cobalt text-white px-8 py-4 rounded-md font-semibold transition-colors"
                >
                  <span>{t('Enviar via WhatsApp', 'Send via WhatsApp')}</span>
                  <ExternalLink className="h-5 w-5" />
                </button>
              </form>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
              <h2 className="text-2xl font-bold text-abyssal">
                {t('Canais diretos', 'Direct channels')}
              </h2>

              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-cta"
                className="flex items-start gap-4 bg-white border border-[#E6E6ED] hover:border-cobalt p-6 rounded-md transition-colors"
              >
                <div className="bg-cobalt p-3 rounded-md flex-shrink-0">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-abyssal">WhatsApp</h3>
                  <p className="text-cobalt font-semibold">+55 27 99648-2098</p>
                  <p className="text-steel text-sm mt-1">
                    {t('Atendimento rápido e direto com nossa equipe.', 'Fast and direct service with our team.')}
                  </p>
                </div>
              </a>

              <div className="bg-white border border-[#E6E6ED] p-6 rounded-md space-y-6">
                <div className="flex items-start gap-4">
                  <Mail className="h-5 w-5 text-cobalt flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-abyssal text-sm">Email</h3>
                    <a href="mailto:costavalente@costavalente.com.br" className="text-steel hover:text-cobalt transition-colors text-sm">
                      costavalente@costavalente.com.br
                    </a>
                  </div>
                </div>
                <div className="h-px bg-[#E6E6ED]"></div>
                <div className="flex items-start gap-4">
                  <MapPin className="h-5 w-5 text-cobalt flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-abyssal text-sm">
                      {t('Endereço', 'Address')}
                    </h3>
                    <p className="text-steel text-sm leading-relaxed mt-1">
                      COSTA VALENTE TRADING LTDA<br />
                      Rod. Gov. Mário Covas, 256, KM 280<br />
                      Portaria B, Sala 168, Padre Mathias<br />
                      Cariacica/ES, CEP 29.157-100<br />
                      CNPJ: 54.517.905/0001-62
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
